'use client'

import { type Control, Controller, type FieldValues, type Path, type UseFormSetValue, useWatch } from 'react-hook-form'

import { GATEWAY_LIST_FILTERS_FIELDS_TEST_IDS } from '../constants/gatewayListFilters.test-ids.const'
import { useGatewayListFilterOptionSets } from '../hooks/useGatewayListFilterOptionSets'
import { useGatewayListFormFilterCoercion } from '../hooks/useGatewayListFormFilterCoercion'
import type { GatewayListFilterTraitsSource } from '../types/gatewayListFilters.types'

import { GatewayListSelectFilter } from './GatewayListSelectFilter'

import styles from './GatewayListFiltersFields.module.scss'

type GatewayListFiltersFieldsProps<T extends FieldValues> = {
  control: Control<T>
  sources: GatewayListFilterTraitsSource[]
  setValue?: UseFormSetValue<T>
  className?: string
  secondaryFiltersDisabled?: boolean
}

export function GatewayListFiltersFields<T extends FieldValues>({
  control,
  sources,
  setValue,
  className,
  secondaryFiltersDisabled = false,
}: GatewayListFiltersFieldsProps<T>) {
  const status = useWatch({ control, name: 'status' as Path<T> })
  const runtime = useWatch({ control, name: 'runtime' as Path<T> })
  const feed = useWatch({ control, name: 'feed' as Path<T> })

  const { statusOptions, runtimeOptions, moduleOptions } = useGatewayListFilterOptionSets(sources, {
    status: String(status ?? 'all'),
    runtime: String(runtime ?? 'all'),
    feed: String(feed ?? 'all'),
    projectKey: 'all',
  })

  useGatewayListFormFilterCoercion({
    setValue,
    statusOptions,
    runtimeOptions,
    moduleOptions,
    filters: {
      status: String(status ?? 'all'),
      runtime: String(runtime ?? 'all'),
      feed: String(feed ?? 'all'),
      projectKey: 'all',
    },
  })

  return (
    <div data-testid={GATEWAY_LIST_FILTERS_FIELDS_TEST_IDS.root} className={`${styles.container} ${className ?? ''}`}>
      <Controller
        name={'status' as Path<T>}
        control={control}
        render={({ field }) => (
          <GatewayListSelectFilter
            value={field.value}
            options={statusOptions}
            disabled={secondaryFiltersDisabled}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name={'runtime' as Path<T>}
        control={control}
        render={({ field }) => (
          <GatewayListSelectFilter
            value={field.value}
            options={runtimeOptions}
            disabled={secondaryFiltersDisabled}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name={'feed' as Path<T>}
        control={control}
        render={({ field }) => (
          <GatewayListSelectFilter
            value={field.value}
            options={moduleOptions}
            disabled={secondaryFiltersDisabled}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  )
}
